import { FormField, Label } from "@justworkshr/milo-form";
import { useTranslation } from "react-i18next";
import { getIn, useFormikContext } from "formik";
import {
  ArrayFieldDataType,
  FieldDataType,
  FieldTypes,
  ValidationType,
} from "./types";
import AutoFormInput from "./AutoFormInput";
import AutoFormFieldArray from "./AutoFormFieldArray";
import AddressForm from "../InternationalAddressForm/AddressForm";
import styles from "./AutoFormField.module.css";
import { ProfileField } from "pages/employer-of-record/configs";
import { fieldErrorHandler } from "pages/employer-of-record/forms/utils/fieldErrorHandler";

type Props = FieldDataType & {
  className?: string | { [key: string]: string };
  optional?: boolean;
  id?: string;
  validations?: ValidationType;
  fields?: Array<FieldDataType>;
  // Override the display of the `Optional` label on a field. Does not impact validations.
  hideOptional?: boolean;
  format?: (value: string) => string;
  compact?: (value: string) => string;
};

export function shouldRequire(
  field: FieldDataType,
  values: Record<string, unknown>
) {
  if (!field.validations?.required) return false;
  if ("enabled" in field.validations?.required) {
    return field.validations.required.enabled;
  }

  for (const entry of Object.entries(field.validations.required.when)) {
    const [name, isRequired] = entry;
    const dependantFieldValue = values[name];
    if (isRequired(dependantFieldValue)) {
      return true;
    }
  }

  return false;
}

const AutoFormField = (props: Props) => {
  const { type, name, label, message, validations, hideOptional } = props;
  const { t } = useTranslation();
  const { values, touched, errors, handleChange, handleBlur } =
    useFormikContext<Record<string, unknown>>();

  const getError = () => {
    const targetName = type === FieldTypes.phoneNumber ? `${name}.value` : name;

    if (getIn(touched, targetName)) {
      return t(getIn(errors, targetName), validations);
    }

    return "";
  };

  const isRequired = () => {
    const enabled = shouldRequire(props, values);

    if (hideOptional === undefined) {
      return enabled;
    } else {
      if (enabled) {
        return true;
      } else {
        return hideOptional;
      }
    }
  };

  if (props.hideWhen) {
    for (const [name, shouldHide] of Object.entries(props.hideWhen)) {
      if (shouldHide(values[name])) return null;
    }
  }

  if ("array" in props && props.array) {
    return <AutoFormFieldArray {...(props as ArrayFieldDataType)} />;
  } else if (props.type === "address") {
    const addressField = props as ProfileField<FieldTypes.address, undefined>;

    return (
      <div>
        {props.label && (
          <div className={styles.addressLabel}>{props.label}</div>
        )}
        <AddressForm
          formPath={props.name}
          handleChange={handleChange}
          handleBlur={handleBlur}
          supportedCountries={addressField.supportedCountries}
        />
      </div>
    );
  } else if (
    props.type === "object" &&
    props.fields &&
    props.fields.length > 0
  ) {
    return (
      <>
        {props.fields.map((subfield) => (
          <div key={subfield.name} className={styles.childrenValueInput}>
            <Label htmlFor={`${props.name}.${subfield.name}`}>
              {t(subfield.label || "")}
            </Label>
            <FormField
              name={`${props.name}.${subfield.name}`}
              aria-labelledby={t(subfield.label || "")}
              message={subfield.message}
              required={shouldRequire(subfield, values)}
              error={fieldErrorHandler(
                `${props.name}.${subfield.name}`,
                errors,
                touched
              )}
            >
              <AutoFormInput
                {...subfield}
                name={`${props.name}.${subfield.name}`}
              />
            </FormField>
          </div>
        ))}
      </>
    );
  } else {
    // TODO: Hide label when input is checkbox
    return (
      <FormField
        name={name}
        label={t(label || "")}
        message={message}
        required={isRequired()}
        error={getError()}
      >
        <AutoFormInput {...props} />
      </FormField>
    );
  }
};

export default AutoFormField;
